import { z } from 'zod';
import { pageSchema, resourceKeySchema, timestampSchema, versionedSchema } from './common';

/**
 * The infrastructure register: the things a project is responsible for, and
 * what state each of them is in.
 *
 * ## Why the type of a thing is data, not an enum
 *
 * This started as IT service monitoring, and said so in its own vocabulary: a
 * `ServiceKind` enum of `application | database | queue | host | endpoint |
 * job`, backed by a PostgreSQL enum type. Every deployment got that list, and
 * only that list, so a project responsible for bridges, culverts, level
 * crossings and signalling had nowhere to put any of them.
 *
 * The fix is not a second, longer enum with railway words in it. That would
 * ship one industry's vocabulary to every deployment and put the product in
 * the business of guessing which nouns a customer uses -- and the product is
 * meant to serve infrastructure programmes, IT estates and situational
 * awareness alike. So **the product now ships no asset vocabulary at all**:
 * types are rows in `InfrastructureAssetType`, created per organization in settings, and
 * the words "bridge" and "database" both live in customer data.
 *
 * The cost is one join and one settings section. The gain is that adding a
 * type is a form submission rather than a migration, an enum value, a
 * translation key in three locales and a release.
 *
 * ## The name
 *
 * The model was called `Service` until the vocabulary stopped being an enum,
 * and then for two phases longer: a bridge is not a service, and the name was
 * a lie every reader of this file had to hold in their head.
 *
 * `Asset` alone is the word a reader sees, and it is taken here -- by stored
 * files (contracts: assets.ts), whose `AssetKind` is `image | file`. Renaming
 * *those* was the alternative and was rejected: their URLs live inside stored
 * Markdown bodies, so it would mean rewriting what people have written, for a
 * gain nobody using the product would ever see.
 *
 * `InfrastructureAsset` is longer and unambiguous, and matches the module it
 * belongs to. The reader-facing word remains whatever the row's `name` says.
 */

/* ------------------------------------------------------------------ types */

/**
 * A kind of thing this organization keeps track of: "Bridge", "Level
 * crossing", "Payments API".
 *
 * Organization-scoped rather than project-scoped, unlike reference lines. A
 * type is vocabulary -- what this customer calls things -- and vocabulary that
 * changed between two projects of the same organization would make the
 * infrastructure register unreadable across them, which is the one thing a
 * register exists to avoid.
 */
export const infrastructureAssetTypeSchema = versionedSchema.extend({
  id: z.string().uuid(),
  key: resourceKeySchema,
  name: z.string(),
  description: z.string().nullable(),
  /** Where it sits in the type filter and the overview. Ties fall back to name. */
  order: z.number().int(),
  /**
   * How many things currently carry this type. Returned so deleting one is an
   * informed act: the register survives the deletion (the reference is set to
   * null, not cascaded), but a reader should know they are about to untype
   * forty bridges.
   */
  assetCount: z.number().int().nonnegative(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type InfrastructureAssetType = z.infer<typeof infrastructureAssetTypeSchema>;

const assetTypeWritableFields = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(500).nullish(),
  order: z.number().int().min(0).max(9999).optional(),
});

export const createInfrastructureAssetTypeSchema = assetTypeWritableFields
  .extend({ key: resourceKeySchema })
  .strict();
export type CreateInfrastructureAssetType = z.infer<typeof createInfrastructureAssetTypeSchema>;

/** `key` is absent: it is quoted in imports and integrations, so it is fixed at
 *  creation for the same reason every other resource key in the product is. */
export const updateInfrastructureAssetTypeSchema = assetTypeWritableFields
  .partial()
  .extend({ version: z.number().int().nonnegative() })
  .strict();
export type UpdateInfrastructureAssetType = z.infer<typeof updateInfrastructureAssetTypeSchema>;

/**
 * The type as it is embedded in a thing that carries one.
 *
 * A projection rather than the whole record: a list of two hundred assets does
 * not want two hundred copies of a description and a version, and a client
 * rendering a row needs exactly the name and something stable to filter by.
 */
export const infrastructureAssetTypeRefSchema = z.object({
  id: z.string().uuid(),
  key: resourceKeySchema,
  name: z.string(),
});
export type InfrastructureAssetTypeRef = z.infer<typeof infrastructureAssetTypeRefSchema>;

/* ----------------------------------------------------------- environments */

/**
 * Which copy of an IT estate a thing belongs to.
 *
 * Kept, and now **optional**. It is a real distinction when the things being
 * tracked are software -- the same application exists three times and only one
 * of them being down is a different sentence -- and meaningless when they are
 * bridges, which exist once. A nullable column says that honestly; a bridge
 * recorded as `production` would be noise every reader has to learn to ignore.
 */
export const environmentSchema = z.enum(['production', 'staging', 'development']);
export type Environment = z.infer<typeof environmentSchema>;

/* ---------------------------------------------------------------- status */

export const infrastructureStatusSchema = z.enum([
  'operational',
  'degraded',
  'outage',
  'maintenance',
  'unknown',
]);
export type InfrastructureStatus = z.infer<typeof infrastructureStatusSchema>;

/** Ordered worst-first so aggregation can take the most severe state. */
export const INFRASTRUCTURE_STATUS_SEVERITY: Record<InfrastructureStatus, number> = {
  outage: 4,
  degraded: 3,
  maintenance: 2,
  unknown: 1,
  operational: 0,
};

/** The most severe status in a group, or `operational` when the group is empty. */
export function worstInfrastructureStatus(
  statuses: Iterable<InfrastructureStatus>,
): InfrastructureStatus {
  let worst: InfrastructureStatus = 'operational';
  for (const status of statuses) {
    if (INFRASTRUCTURE_STATUS_SEVERITY[status] > INFRASTRUCTURE_STATUS_SEVERITY[worst])
      worst = status;
  }
  return worst;
}

/* --------------------------------------------------------------- the row */

export const infrastructureAssetSchema = z.object({
  id: z.string().uuid(),
  key: resourceKeySchema,
  name: z.string(),
  /**
   * Null when the organization has not typed this one yet -- which every row
   * is, for a moment, after a type is deleted. The register keeps the thing
   * and forgets what it was called; losing a bridge because somebody tidied a
   * vocabulary list would be the worse failure.
   */
  type: infrastructureAssetTypeRefSchema.nullable(),
  environment: environmentSchema.nullable(),
  provider: z.string().nullable(),
  status: infrastructureStatusSchema,
  latencyMs: z.number().int().nonnegative().nullable(),
  /**
   * Null when the asset has never reported. The frontend must distinguish
   * "healthy" from "never observed" -- the empty state required of every
   * component by section 9.3.
   */
  lastCheckedAt: timestampSchema.nullable(),
  projectId: z.string().uuid().nullable(),
});
export type InfrastructureAsset = z.infer<typeof infrastructureAssetSchema>;

export const infrastructureAssetPageSchema = pageSchema(infrastructureAssetSchema);

export const infrastructureAssetListQuerySchema = z.object({
  environment: environmentSchema.optional(),
  status: infrastructureStatusSchema.optional(),
  /** By type id rather than key: the filter is populated from the type list the
   *  client just fetched, and an id cannot collide across a rename. */
  typeId: z.string().uuid().optional(),
  projectId: z.string().uuid().optional(),
  search: z.string().max(120).optional(),
  cursor: z.string().max(256).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});

export const statusSampleSchema = z.object({
  observedAt: timestampSchema,
  status: infrastructureStatusSchema,
  latencyMs: z.number().int().nonnegative().nullable(),
});
export type StatusSample = z.infer<typeof statusSampleSchema>;

/**
 * Bounded by construction: a window and a bucket size, never "give me
 * everything". Section 10.1 forbids unbounded responses, and a time series is
 * the easiest place to accidentally return a million rows.
 */
export const timeSeriesQuerySchema = z.object({
  window: z.enum(['1h', '6h', '24h', '7d', '30d']).default('24h'),
  bucket: z.enum(['1m', '5m', '15m', '1h', '6h', '1d']).optional(),
});
export type TimeSeriesQuery = z.infer<typeof timeSeriesQuerySchema>;

export const timeSeriesPointSchema = z.object({
  bucketStart: timestampSchema,
  avgLatencyMs: z.number().nullable(),
  p95LatencyMs: z.number().nullable(),
  sampleCount: z.number().int().nonnegative(),
  worstStatus: infrastructureStatusSchema,
});
export type TimeSeriesPoint = z.infer<typeof timeSeriesPointSchema>;

export const timeSeriesSchema = z.object({
  assetId: z.string().uuid(),
  window: z.string(),
  bucket: z.string(),
  points: z.array(timeSeriesPointSchema),
});
export type TimeSeries = z.infer<typeof timeSeriesSchema>;
